import { useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { Helmet } from 'react-helmet';
import { Grid, Stack } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';

import { authContext, Profile } from '@staffedupusa/shared-components';
import { setUserPropertiesToAmplitude } from '@utils/amplitudeUserProperties';
import useDebounce from '@hooks/UseDebounce';

import { useGetEnterpriseApplications } from '@features/applications/hooks/useGetEnterpriseApplications';
import { useEnterprisreSearchAppliations } from '@features/applications/hooks/useEnterprisreSearchAppliations';

import {
  Application,
  ApplicationStatus,
  DatePeriod,
} from '@features/applications';

import ApplicationsListFilter from '@features/applications/components/ApplicationsListFilters/ApplicationsListFilters';
import EnterpriseApplicationsCountsList from '@features/applications/components/EnterpriseApplicationsCountsList/EnterpriseApplicationsCountsList';
import EnterpriceApplicationsList from '@features/applications/components/EnterpriceApplicationsList/EnterpriceApplicationsList';
import AddApplicantManually from '@features/applications/components/AddApplicantManually/AddApplicantManually';
import LocationSearch from '@features/applications/components/LocationSearch/LocationSearch';

import EnterpriseMetricsHeader from '@features/enterpriseMetrics/components/EnterpriseMetricsHeader/EnterpriseMetricsHeader';
import EnterPriseNotifications from '@features/enterpriseNotifications/components/Notifications/Notifications';
import BoostButton from '@features/boostMyJobs/components/BoostButton/BoostButton';
import CompanyLogoAndCover from '@features/company/components/CompanyLogoAndCover/CompanyLogoAndCover';
import SelectDateModal from '@components/SelectDateModal/SelectDateModal';

dayjs.extend(isSameOrAfter);

const EnterPriseDashboard = () => {
  const location = useLocation();
  const { authData } = useContext(authContext);
  const [hasMounted, setHasMounted] = useState(false);
  const [status, setStatus] = useState<ApplicationStatus | string>('All');
  const [isWOTCOnly, setIsWOTCOnly] = useState<boolean>(false);
  const [positions, setPositions] = useState<string[]>([]);
  const [rating, setRating] = useState<'All' | number>('All');
  const [selectedInterviewType, setSelectedInterviewType] = useState('');
  const [searchValue, setSearchValue] = useState<string>('');
  const [applicationsListAlertText, setApplicationsListAlertText] =
    useState<string>('');
  const [selectedDatePeriod, setSelectedDatePeriod] =
    useState<DatePeriod>('ALL');
  const [date, setDate] = useState<Dayjs | string | null>(null);
  const [isCustomDateModalOpen, setIsCustomDateModalOpen] =
    useState<boolean>(false);
  const [selectedTag, setSelectedTag] = useState('All');
  const [pageNo, setPageNo] = useState(1);
  const [triggerChange, setTriggerChange] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | number>(
    '',
  );
  const debounceValue = useDebounce(searchValue, 500);
  const queryParams = new URLSearchParams(location.search);
  const preSelectedTag = queryParams.get('selected-tag');
  const preSelectedCompany = queryParams.get('selected-company');

  const { getApplications, applicationsData, isLoadingApplications } =
    useGetEnterpriseApplications({
      selectedInterviewType,
      status,
      date,
      pageNo,
      isWOTCOnly,
      rating,
      positions,
      selectedTag,
      enabled: searchValue.length < 3,
      selectedCompanyId,
      setAlertText: setApplicationsListAlertText,
    });
  const { searchApplicationsData, isSearchingApplications } =
    useEnterprisreSearchAppliations({
      searchValue: debounceValue,
      setAlertText: setApplicationsListAlertText,
    });

  const handlePageNo = (newPageNo: number) => {
    setPageNo(newPageNo);
  };

  const filterApplicationsByDate = (applications: Application[]) => {
    if (date) {
      applications.filter((application: Application) => {
        const applicationDate = dayjs(application.dt_timestamp);
        return selectedDatePeriod === 'custom date'
          ? applicationDate.isSame(date, 'day')
          : applicationDate.isSameOrAfter(date, 'day');
      });
    } else return applications;
  };

  const getApplicationsList = () => {
    if (searchApplicationsData?.object) {
      return searchApplicationsData?.object;
    }
    if (applicationsData?.object) {
      // return filterApplicationsByDate(applicationsData.object);
      return applicationsData.object;
    }
    return [];
  };
  const handleSelectedInterviewTypeChange = (type: string) => {
    setSelectedInterviewType(type);
  }
  const getAllApplicationsIds = () => {
    if (applicationsData?.application_ids) {
      return applicationsData.application_ids;
    }
  };

  const getApplicationsCount = () => {
    if (searchApplicationsData?.object) {
      return searchApplicationsData?.object?.length ?? 0;
    }
    if (applicationsData?.total_applications) {
      return applicationsData.total_applications;
    }
    return 0;
  };

  const getDateForFiltration = () => {
    let dateToSet: Dayjs | string | null = dayjs();

    switch (selectedDatePeriod) {
      case 'ALL':
        dateToSet = null;
        break;
      case 'TODAY':
        dateToSet = 'TODAY';
        break;
      case 'YESTERDAY':
        dateToSet = 'YESTERDAY';
        break;
      case 'WEEK':
        dateToSet = 'WEEK';
        break;
      case 'MONTH':
        dateToSet = 'MONTH';
        break;
      case 'custom date':
        dateToSet = null;
        setIsCustomDateModalOpen(true);
        break;
      default:
        break;
    }
    setDate(dateToSet);
  };

  const handleTagsChange = (event: {
    target: { value: React.SetStateAction<string> };
  }) => {
    setSelectedTag(event.target.value);
  };

  useEffect(() => getDateForFiltration(), [selectedDatePeriod]);

  useEffect(() => {
    setPositions([]);
  }, [selectedCompanyId]);

  useEffect(() => {
    getApplications();
  }, [status, date, isWOTCOnly, positions, rating, selectedTag]);

  useEffect(() => {
    if (!hasMounted) {
      setHasMounted(true);
      return;
    }
    setTriggerChange(true);
    setPageNo(1);
  }, [status, date, isWOTCOnly, positions, rating, selectedTag]);

  useEffect(() => {
    preSelectedTag ? setSelectedTag(preSelectedTag) : null;
    preSelectedCompany ? setSelectedCompanyId(preSelectedCompany) : null;
  }, []);

  useEffect(() => {
    if (authData?.user && authData?.user?.enterprise) {
      setUserPropertiesToAmplitude(
        authData.user as Profile & {
          sub_users_count: number;
          enterprise_companies_count: number;
        },
      );
    }
  }, [authData]);

  return (
    <>
      <Helmet>
        <title>Dashboard | StaffedUp</title>
      </Helmet>
      <Stack spacing={5} pt={5} pb={4} px={3} pl={6}>
        <EnterPriseNotifications />
        <Grid container sx={{ marginTop: '0 !important' }}>
          <Grid item xs={12} md={3} pr={1} pb={3} mb={12}>
            <CompanyLogoAndCover />
            <EnterpriseApplicationsCountsList
              selectedPositions={positions}
              setSelectedPositions={setPositions}
              disableButtons={searchValue.length > 2}
              handlePageNo={handlePageNo}
              selectedCompanyId={selectedCompanyId}
            />
          </Grid>
          <Grid item xs={12} md={9} pl={1}>
            <EnterpriseMetricsHeader />
            <Stack
              alignItems="center"
              p={2}
              spacing={2}
              mt={4}
              borderRadius={8}
              bgcolor="white"
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              width={'100%'}
            >
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={12} lg={12} xl={6}>
                  <LocationSearch
                    setSelectedCompanyId={setSelectedCompanyId}
                    selectedCompanyId={selectedCompanyId}
                    handlePageNo={handlePageNo}
                    disableButtons={searchValue.length > 2}
                  />
                </Grid>
                <Grid item xs={12} md={12} lg={12} xl={6} container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <BoostButton />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <AddApplicantManually
                      handlePageNo={handlePageNo}
                      setTriggerChange={setTriggerChange}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Stack>
            <ApplicationsListFilter
              selectedStatus={status}
              setSelectedStatus={setStatus}
              rating={rating}
              setRating={setRating}
              searchValue={searchValue}
              setSearchValue={setSearchValue}
              disableButtons={searchValue.length > 2}
              selectedDatePeriod={selectedDatePeriod}
              setSelectedDatePeriod={setSelectedDatePeriod}
              openCustomDateModal={() => setIsCustomDateModalOpen(true)}
              handlePageNo={handlePageNo}
              isWOTCOnly={isWOTCOnly}
              setIsWOTCOnly={setIsWOTCOnly}
              selectedTag={selectedTag}
              handleTagsChange={handleTagsChange}
              selectedCompanyId={selectedCompanyId}
              handleSelectedInterviewTypeChange={handleSelectedInterviewTypeChange}
              selectedInterviewType={selectedInterviewType}
            />
            <EnterpriceApplicationsList
              handlePageNo={handlePageNo}
              triggerChange={triggerChange}
              setTriggerChange={setTriggerChange}
              selectedStatus={status}
              pageNo={pageNo}
              applications={getApplicationsList()}
              isLoading={isLoadingApplications || isSearchingApplications}
              isWOTCOnly={isWOTCOnly}
              setIsWOTCOnly={setIsWOTCOnly}
              searchValue={searchValue}
              alertText={applicationsListAlertText}
              disableButtons={searchValue.length > 2}
              handleTagsChange={handleTagsChange}
              setSearchValue={setSearchValue}
              selectedTag={selectedTag}
              selectedCompanyId={selectedCompanyId}
              totalAplications={getApplicationsCount()}
              allApplicationsIds={getAllApplicationsIds()}
            />
          </Grid>
        </Grid>
      </Stack>
      <SelectDateModal
        isOpen={isCustomDateModalOpen}
        handleClose={() => setIsCustomDateModalOpen(false)}
        date={date}
        setDate={setDate}
      />
    </>
  );
};

export default EnterPriseDashboard;
